/**
 * Utility functions for handling inline images in task descriptions.
 * 
 * The pattern is:
 * - Description contains: <img src="file-service://{fileId}" />
 * - textFiles array contains: [{ fileId, width, height }]
 * 
 * This utility converts file-service:// references to actual image URLs.
 */

import apiClient from "./apiClient";
import {
  STORAGE_BASE_URL,
  PM_SERVICE_BASE_URL,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
} from "../constants";

/**
 * Clean up broken/malformed image tags from HTML
 * @param {string} html - The HTML content
 * @returns {string} - Cleaned HTML
 */
export function cleanupBrokenImages(html) {
  if (!html) return html;
  
  // Remove images with empty, invalid, or malformed src
  return html.replace(/<img[^>]*src="[^"]*"[^>]*>/gi, (match) => {
    // Check for problematic src patterns
    const srcMatch = match.match(/src="([^"]*)"/);
    if (!srcMatch) return match;
    
    const src = srcMatch[1];
    
    // Skip if src is empty, ":0", "//:0", or just "data:"
    if (!src || src === ':0' || src === '//:0' || src === 'data:' || src === 'data:') {
      return ''; // Remove broken image
    }
    
    return match;
  });
}

/**
 * Process description HTML and replace file-service:// references with actual URLs.
 * @param {string} descriptionHtml - The HTML content with file-service:// references
 * @param {Array} textFiles - Array of { fileId, width, height }
 * @returns {Promise<string>} - Processed HTML with actual image URLs
 */
const arrayPattern = /\[([0-9a-fA-F-]{36})\s*,\s*(\d+)\s*,\s*(\d+)\]/g;

export function convertArrayRefsToFileService(html = "") {
  if (!html) return html;
  return html.replace(arrayPattern, (_m, id, w, h) => {
    return `<img src="file-service://${id}" width="${w}" height="${h}" alt="image" />`;
  });
}

export function stripImagesToAltText(html = "", fallback = "[image]") {
  if (!html) return html;
  return html.replace(/<img\b[^>]*>/gi, (match) => {
    const altMatch = match.match(/alt=["']([^"']+)["']/i);
    if (altMatch && altMatch[1]?.trim()) return altMatch[1].trim();
    return fallback;
  });
}

export function convertFileServiceImagesToArrayRefs(html = "") {
  if (!html) return html;
  return html.replace(/<img\b[^>]*file-service:\/\/([0-9a-fA-F-]{36})[^>]*>/gi, (match, id) => {
    const widthMatch = match.match(/width=["']?(\d+)["']?/i);
    const heightMatch = match.match(/height=["']?(\d+)["']?/i);
    const width = widthMatch ? parseInt(widthMatch[1], 10) || 1 : 1;
    const height = heightMatch ? parseInt(heightMatch[1], 10) || 1 : 1;
    return `[${id}, ${width}, ${height}]`;
  });
}

export async function processDescriptionImages(descriptionHtml, textFiles) {
  if (!descriptionHtml) {
    return descriptionHtml;
  }

// Convert stored array form [uuid, w, h] into file-service img tags for uniform processing
descriptionHtml = convertArrayRefsToFileService(descriptionHtml);

  console.info("[taskUtils] initial description for processing", {
    length: descriptionHtml.length,
    sample: descriptionHtml.slice(0, 200),
    textFilesCount: textFiles?.length || 0,
    tokenPresent: !!sessionStorage.getItem("authToken"),
  });

  // First, clean up any broken images
  let processedHtml = cleanupBrokenImages(descriptionHtml);
  
  const token =
    sessionStorage.getItem("authToken") || localStorage.getItem("authToken");

  const buildAuthHeaders = (includeClientName = false) => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (includeClientName) {
      headers[CLIENT_NAME_HEADER_KEY] = CLIENT_NAME_HEADER_VALUE;
    }
    return headers;
  };

  const resolveResponseUrl = (payload, visited = new WeakSet()) => {
    if (!payload) return null;
    if (typeof payload === "string") return payload;
    if (typeof payload !== "object") return null;
    if (visited.has(payload)) return null;
    visited.add(payload);

    if (Array.isArray(payload)) {
      for (const entry of payload) {
        const resolved = resolveResponseUrl(entry, visited);
        if (resolved) return resolved;
      }
      return null;
    }

    const directStringFields = ["uri", "url", "downloadUrl", "download_url", "signedUrl"];
    for (const field of directStringFields) {
      const value = payload[field];
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }

    const arrayFields = ["signedUrls", "urls", "downloadUrls"];
    for (const field of arrayFields) {
      const value = payload[field];
      if (Array.isArray(value)) {
        const resolved = resolveResponseUrl(value, visited);
        if (resolved) return resolved;
      }
    }

    if (payload.data) {
      const resolved = resolveResponseUrl(payload.data, visited);
      if (resolved) return resolved;
    }

    if (payload.result) {
      const resolved = resolveResponseUrl(payload.result, visited);
      if (resolved) return resolved;
    }

    for (const [key, value] of Object.entries(payload)) {
      if (typeof value === "string") {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey.includes("url") || normalizedKey.includes("uri") || normalizedKey.includes("signed")) {
          return value;
        }
        continue;
      }
      if (typeof value === "object" && value !== null) {
        const resolved = resolveResponseUrl(value, visited);
        if (resolved) return resolved;
      }
    }

    return null;
  };

  const fetchFileUrl = async (fileId) => {
    const storageHeaders = {
      ...buildAuthHeaders(true),
    };

    const signedUrlEndpoint = `${STORAGE_BASE_URL}/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(
      fileId,
    )}`;

    try {
      const response = await apiClient.get(signedUrlEndpoint, { headers: storageHeaders });
      const signedUrl = resolveResponseUrl(response?.data);
      if (signedUrl) return signedUrl;
    } catch (err) {
      console.error("[taskUtils] signed-urls fetch failed", { fileId, error: err?.message });
    }

    // try storage-service first
    try {
      const resp = await apiClient.get(
        `${STORAGE_BASE_URL}/api/v1/files/${fileId}`,
        { headers: storageHeaders }
      );
      const url = resolveResponseUrl(resp?.data);
      if (url) return url;
    } catch (err) {
      console.error("[taskUtils] storage-service fetch failed", {
        fileId,
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
    }

    // fallback: pm-service proxy (FileController)
    try {
      const resp = await apiClient.get(`${PM_SERVICE_BASE_URL}/api/v1/files/${fileId}`, {
        headers: buildAuthHeaders(true),
      });
      const url = resolveResponseUrl(resp?.data);
      if (url) return url;
    } catch (err) {
      console.error("[taskUtils] pm-service fetch failed", {
        fileId,
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
    }

    return null;
  };

  // Find all file-service:// references in the HTML
  const fileServiceRegex = /file-service:\/\/([a-f0-9-]+)/gi;
  const matches = [...processedHtml.matchAll(fileServiceRegex)];
  
  if (matches.length === 0) {
    return processedHtml;
  }

  // Get unique fileIds
  const uniqueFileIds = [...new Set(matches.map(m => m[1]))];
  console.info("[taskUtils] found file-service refs", { uniqueFileIds });
  
  // Fetch metadata for all files
  const fileUrls = new Map();
  
  for (const fileId of uniqueFileIds) {
    const url = await fetchFileUrl(fileId);
    if (url) {
      console.info("[taskUtils] file URL fetched", { fileId, url });
      fileUrls.set(fileId, url);
    } else {
      console.error("[taskUtils] Failed to get URL for file", fileId, {
        message: "no url from storage or pm service",
      });
      const brokenRegex = new RegExp(`<img[^>]*file-service://${fileId}[^>]*>`, "gi");
      const fallback = `<img src="file-service://${fileId}" alt="image unavailable" />`;
      processedHtml = processedHtml.replace(brokenRegex, fallback);
    }
  }

  // Replace all file-service:// references with actual URLs
  for (const [fileId, url] of fileUrls) {
    const regex = new RegExp(`file-service://${fileId}`, 'gi');
    processedHtml = processedHtml.replace(regex, url);
  }

  // Final fallback: if still no replacements and the HTML still contains array form, normalize once more
  if (processedHtml.includes('[') && processedHtml.includes(']')) {
    const normalized = convertArrayRefsToFileService(processedHtml);
    if (normalized !== processedHtml) {
      processedHtml = normalized;
      console.info("[taskUtils] final fallback array->img applied");
    }
  }

  console.info("[taskUtils] processed description complete", {
    length: processedHtml.length,
    sample: processedHtml.slice(0, 200),
    fileUrlsResolved: [...fileUrls.keys()],
  });

  return processedHtml;
}

/**
 * Synchronous version that assumes URLs are already known (for caching)
 * @param {string} descriptionHtml 
 * @param {Object} urlMap - Map of fileId -> URL
 * @returns {string}
 */
export function processDescriptionImagesSync(descriptionHtml, urlMap) {
  if (!descriptionHtml || !urlMap || urlMap.size === 0) {
    return descriptionHtml;
  }

  // Convert stored array form [uuid, w, h] into file-service img tags
  let processedHtml = convertArrayRefsToFileService(descriptionHtml);

  for (const [fileId, url] of urlMap) {
    const regex = new RegExp(`file-service://${fileId}`, 'gi');
    processedHtml = processedHtml.replace(regex, url);
  }

  console.info("[taskUtils] processed description sync complete", {
    length: processedHtml.length,
    sample: processedHtml.slice(0, 200),
  });

  return processedHtml;
}
